import { useEffect, useRef } from 'react';

import { useLazyFetchGraphql } from '@zoopla/fetch-graphql';
import { AuthType } from '../../../graphql.generated';
import { FacebookUserPayload } from '../../../../global';
import {
    SocialSignInFacebookV2Document,
    SocialSignInFacebookV2Mutation,
    SocialSignInFacebookV2MutationVariables
} from '../queries/facebookSignIn.generated';
import { getNextBestAction } from '../utils/getNextBestAction';
import {
    logAuthSuccess,
    logNetworkAuthFailure,
    logSocialAuthFailure,
    logUserAuthFailure,
    trackAuthFailure,
    trackAuthRememberMe,
    trackAuthSuccess
} from './authMutationHook.tracking';
import { AuthMutationHook } from './authMutationHook.types';
import { useAuth } from './useAuth';

export const useFacebook: AuthMutationHook<boolean | undefined> = ({
    trackingData
}) => {
    const hasNetworkError = useRef(false);
    const isFacebookLoaded = useRef(false);
    const { setCurrentSession } = useAuth();
    const { lazyFetch: signInMutation } = useLazyFetchGraphql<
        SocialSignInFacebookV2Mutation,
        SocialSignInFacebookV2MutationVariables
    >(SocialSignInFacebookV2Document, {
        onError(error) {
            if (error && 'isNetworkError' in error) {
                hasNetworkError.current = true;
            }
        }
    });

    const loadSdk = () => {
        const element = document.getElementsByTagName('script')[0];
        const sdk = document.createElement('script');
        sdk.src = `https://connect.facebook.net/en_US/sdk.js`;
        element?.parentNode?.insertBefore(sdk, element);
    };

    const init = () => {
        // when the Facebook SDK finishes loading asynchronously it calls this function
        window.fbAsyncInit = () => {
            window.FB?.init({
                appId: '205940246178878',
                oauth: true,
                cookie: true, // enable cookies to allow the server to access the session
                version: 'v16.0'
            });
            isFacebookLoaded.current = true;
        };

        loadSdk();
    };

    const trackError = () => {
        trackAuthFailure({
            source: 'facebook',
            type: 'signin',
            label: trackingData.label,
            segmentData: trackingData.segmentData
        });
    };

    const trackAndThrowError = (message: string, requestId?: string) => {
        trackError();

        const requestIdMessage = requestId ? ` (Request ID: ${requestId})` : '';

        throw new Error(`${message}${requestIdMessage}`);
    };

    const authenticationMutation = async (payload: {
        accessToken: string;
        rememberMe: boolean;
    }) => {
        const response = await signInMutation(payload);

        if (hasNetworkError.current) {
            logNetworkAuthFailure({ type: 'facebook' });
            return trackAndThrowError(
                'Sorry, something went wrong. Please try again. (Network error)'
            );
        }

        if (!response.data || response.errors) {
            return trackAndThrowError(
                'Sorry, something went wrong. Please try again.',
                response.extensions?.requestId
            );
        }

        const { data } = response;

        if (data.socialSignInFacebookV2?.__typename === 'AuthError') {
            return trackAndThrowError(
                'Sorry, something went wrong. Please try again.',
                response.extensions?.requestId
            );
        }

        if (!data.socialSignInFacebookV2?.user) {
            return trackAndThrowError(
                'Sorry, something went wrong. Please try again.',
                response.extensions?.requestId
            );
        }

        // This covers the case we have seen in a few incidents with sessions
        // when the authentication API works well but the user one fails
        if (
            !data.socialSignInFacebookV2.user.loggedIn ||
            !data.socialSignInFacebookV2.user.userUuid
        ) {
            logUserAuthFailure({
                type: 'facebook',
                subtype: !data.socialSignInFacebookV2.user.userUuid
                    ? 'no_uuid'
                    : 'not_logged_in'
            });

            return trackAndThrowError(
                'Sorry, something went wrong. Please try again later.',
                response.extensions?.requestId
            );
        }

        const {
            socialSignInFacebookV2: { authType, user }
        } = data;

        const isNewUserAuthentication = authType === AuthType.Register;

        const type = isNewUserAuthentication ? 'registration' : 'signin';

        logAuthSuccess('facebook-auth');
        trackAuthSuccess({
            source: 'facebook',
            type,
            userObject: user,
            label: trackingData.label,
            segmentData: trackingData.segmentData
        });

        if (payload?.rememberMe) {
            trackAuthRememberMe({
                source: 'facebook',
                type,
                userObject: user,
                label: trackingData.label,
                segmentData: trackingData.segmentData
            });
        }

        setCurrentSession(user, isNewUserAuthentication);

        const nextBestActions = getNextBestAction(user);

        return { ...user, actions: nextBestActions };
    };

    const authenticate = async (rememberMe?: boolean) => {
        if (!isFacebookLoaded.current) {
            init();
        }

        return new Promise<ReturnType<typeof authenticationMutation>>(
            (res, rej) => {
                window.FB?.login(
                    function (response: FacebookUserPayload) {
                        if (
                            response.status === 'connected' &&
                            response?.authResponse
                        ) {
                            return res(
                                authenticationMutation({
                                    accessToken:
                                        response.authResponse.accessToken,
                                    rememberMe: rememberMe || false
                                })
                            );
                        } else {
                            const type: 'not_authorized' | 'unknown' =
                                response.status === 'not_authorized'
                                    ? response.status
                                    : 'unknown';

                            logSocialAuthFailure({
                                name: 'facebook-auth',
                                type
                            });

                            trackError();

                            return rej(
                                new Error(
                                    'Something went wrong and you have not been signed in. Please try again.'
                                )
                            );
                        }
                    },
                    { scope: 'public_profile,email' }
                );
            }
        );
    };

    useEffect(() => {
        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return authenticate;
};
